import { Injectable, EventEmitter } from '@angular/core';
import { Image } from '../model/image';
import { Observable, pipe } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DocumentTypeMimeTypes } from '../model/const/document-type-mime-types';
import { FormGroup } from '@angular/forms';

export interface IFileValidator {
  validateDocument(document: File): string;
}

export interface IFileUploadManagerMini {
  uploadFile(file: any, parentEntityId?: string): Observable<string>;
  downloadFile(id: string): Observable<File>;
  deleteFile(id: string): Observable<void>;
}

export interface IFileControlHelper {
  fillFileControls(formGroup: FormGroup, file: File, callback?: () => void);
}

@Injectable()
export class PDFValidator implements IFileValidator {
  validateDocument(document: File): string {
    if (document.type) {
      return ['application/pdf'].includes(document.type) ? null : 'DOCUMENT.VALID_EXTENSIONS_PDF';
    } else {
      const source = document.name.split('.');
      return ['pdf'].includes(source[source.length - 1]) ? null : 'DOCUMENT.VALID_EXTENSIONS_PDF';
    }
  }
}

@Injectable()
export class FileService implements IFileValidator {
  constructor(private httpClient: HttpClient) {}

  private _validateImages(files: FileList): boolean {
    const types = ['image/jpeg', 'image/png'];
    for (let i = 0; i < files.length; i++) {
      if (!types.includes(files[i].type)) return false;
    }
    return true;
  }

  loadFileBlob(url: string): Observable<Blob> {
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  getImage(files: FileList): Observable<Image> {
    if (files && files.length === 1) {
      if (!this._validateImages(files)) {
        console.log('Invalid file format.');
        return;
      }

      const emitter = new EventEmitter<Image>();
      let reader = new FileReader();
      reader.onload = (event: any) => {
        emitter.next(new Image(event.target.result, files[0]));
      };
      reader.readAsDataURL(files[0]);
      return emitter.asObservable();
    }

    throw Error('Invalid file list.');
  }

  getExtension(fileName: string): string {
    if (!fileName) {
      return '';
    }
    const source = fileName.split('.');
    return source.length > 1 ? source[source.length - 1] : '';
  }

  isPdf(mimeTypeOrFileName: string): boolean {
    return (
      DocumentTypeMimeTypes.PDF().includes(mimeTypeOrFileName.toLowerCase()) ||
      this.getExtension(mimeTypeOrFileName).toLowerCase() === 'pdf'
    );
  }

  isSpreadsheet(mimeTypeOrFileName: string) {
    return (
      DocumentTypeMimeTypes.SpreadSheet().includes(mimeTypeOrFileName.toLowerCase()) ||
      ['xls', 'xlsx'].includes(this.getExtension(mimeTypeOrFileName).toLowerCase())
    );
  }

  isWordProcessor(mimeTypeOrFileName: string): boolean {
    return (
      DocumentTypeMimeTypes.WordProcessor().includes(mimeTypeOrFileName.toLowerCase()) ||
      ['rtf', 'doc', 'docx'].includes(this.getExtension(mimeTypeOrFileName).toLowerCase())
    );
  }

  isWebImage(mimeTypeOrFileName: string): boolean {
    return (
      DocumentTypeMimeTypes.WebImage().includes(mimeTypeOrFileName.toLowerCase()) ||
      ['png', 'jpeg', 'jpg', 'jfif'].includes(this.getExtension(mimeTypeOrFileName).toLowerCase())
    );
  }

  validateDocument(document: File): string {
    if (document.type) {
      return DocumentTypeMimeTypes.PDF().includes(document.type.toLowerCase()) ||
        DocumentTypeMimeTypes.WebImage().includes(document.type.toLowerCase()) ||
        DocumentTypeMimeTypes.SpreadSheet().includes(document.type.toLowerCase()) ||
        DocumentTypeMimeTypes.WordProcessor().includes(document.type.toLowerCase())
        ? null
        : 'DOCUMENT.VALID_EXTENSIONS';
    } else {
      return ['pdf', 'rtf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpeg', 'jpg', 'jfif'].includes(
        this.getExtension(document.name).toLowerCase()
      )
        ? null
        : 'DOCUMENT.VALID_EXTENSIONS';
    }
  }
}
